<template>
    <div>
        <h5>Billing details</h5>

        <ValidationObserver class="row" ref="form">
            <ValidationProvider class="col-md-6 mb-3" name="First Name" rules="required|alpha_spaces|min:2|max:20" v-slot="{ errors }">
                <label for="first_name" class="form-label">First Name*</label>
                <div id="first_name" class="form-group mb-0">
                    <input
                        type="text"
                        :disabled="isDisabled"
                        :class="['form-control mb-1', {'input-error' : errors[0] }]"
                        v-model="billing.first_name"
                    >
                </div>

                <div :class="['form-group field-validation-error', {'animated shake': errors[0] && animated }]" >
                    {{ errors[0] }}
                </div>
            </ValidationProvider>

            <ValidationProvider class="col-md-6 mb-3" name="Last Name" rules="required|alpha_spaces|min:2|max:20" v-slot="{ errors }">
                <label for="last_name" class="form-label">Last Name*</label>
                <div id="last_name" class="form-group mb-0">
                    <input
                        type="text"
                        :disabled="isDisabled"
                        :class="['form-control mb-1', {'input-error' : errors[0] }]"
                        v-model="billing.last_name"
                    >
                </div>

                <div :class="['form-group field-validation-error', {'animated shake': errors[0] && animated }]" >
                    {{ errors[0] }}
                </div>
            </ValidationProvider>

            <div class="col-md-12 mb-3">
                <label for="company" class="form-label">Company Name (Optional):</label>
                <div id="company" class="form-group mb-0">
                    <input
                        type="text"
                        :disabled="isDisabled"
                        class="form-control"
                        v-model="billing.company_name"
                    >
                </div>
            </div>

            <ValidationProvider class="col-md-12 mb-3" name="Street Address" rules="required|min:2|max:50" v-slot="{ errors }">
                <label class="form-label">Street Address:*</label>
                <div class="form-group mb-0">
                    <input
                        type="text"
                        :disabled="isDisabled"
                        :class="['form-control mb-1', {'input-error' : errors[0] }]"
                        v-model="billing.street"
                    >
                </div>

                <div :class="['form-group field-validation-error', {'animated shake': errors[0] && animated }]" >
                    {{ errors[0] }}
                </div>
            </ValidationProvider>

            <ValidationProvider class="col-md-6 mb-3" name="Country" rules="required" v-slot="{ errors }">
                <div class="form-group mb-0">
                    <label for="country" class="form-label">Country*</label>
                    <div class="form-group">
                        <select
                            id="country"
                            :disabled="isDisabled"
                            :class="['custom-select mb-1', {'input-error' : errors[0] }]"
                            v-model="billing.country"
                        >
                            <option :value="null" disabled>Select One</option>
                            <option value="United States">United States</option>
                            <option
                                v-for="country in countries"
                                :value="country.name"
                                :key="country.id"
                                v-if="country.id !== 226"
                            >
                                {{ country.name }}
                            </option>
                        </select>
                    </div>

                    <div :class="['form-group field-validation-error', {'animated shake': errors[0] && animated }]" >
                        {{ errors[0] }}
                    </div>
                </div>
            </ValidationProvider>

            <ValidationProvider class="col-md-6 mb-3" name="City" rules="required|min:4" v-slot="{ errors }">
                <label for="city" class="form-label">City*</label>
                <div class="form-group mb-0">
                    <input
                        id="city"
                        type="text"
                        :disabled="isDisabled"
                        :class="['form-control mb-1', {'input-error' : errors[0] }]"
                        v-model="billing.city"
                    >
                </div>

                <div :class="['form-group field-validation-error', {'animated shake': errors[0] && animated }]" >
                    {{ errors[0] }}
                </div>
            </ValidationProvider>

            <ValidationProvider class="col-md-6 mb-3" name="State" rules="required" v-slot="{ errors }">
                <label for="state" class="form-label">State*</label>
                <div class="form-group mb-0">
                    <select
                        id="state"
                        :disabled="isDisabled"
                        :class="['custom-select mb-1', {'input-error' : errors[0] }]"
                        v-model="billing.state"
                    >
                        <option :value="null" disabled>Select One</option>
                        <option v-for="state in states" :value="state.name" :key="state.id">{{ state.name }}</option>
                    </select>
                </div>

                <div :class="['form-group field-validation-error', {'animated shake': errors[0] && animated }]" >
                    {{ errors[0] }}
                </div>
            </ValidationProvider>

            <ValidationProvider class="col-md-6 mb-3" name="Zip" rules="required|zip" v-slot="{ errors }">
                <label for="zip_code" class="form-label">Zip Code*</label>
                <div class="form-group mb-0">
                    <input
                        id="zip_code"
                        type="text"
                        :disabled="isDisabled"
                        :class="['form-control mb-1', {'input-error' : errors[0] }]"
                        v-mask="['#####','#####-####']"
                        v-model="billing.zip_code"
                    >
                </div>

                <div :class="['form-group field-validation-error', {'animated shake': errors[0] && animated }]" >
                    {{ errors[0] }}
                </div>
            </ValidationProvider>

            <ValidationProvider class="col-md-6 mb-3" name="Phone Number" rules="required|us_phone_number" v-slot="{ errors }">
                <label for="phone" class="form-label">Phone Number*</label>
                <div class="form-group mb-0">
                    <input
                        type="text"
                        id="phone"
                        aria-label="phone number"
                        :disabled="isDisabled"
                        :class="['form-control mb-1', {'input-error' : errors[0] }]"
                        v-mask="'+1(###)-###-####'"
                        v-model="billing.phone"
                    >
                </div>

                <div :class="['form-group field-validation-error', {'animated shake': errors[0] && animated }]" >
                    {{ errors[0] }}
                </div>
            </ValidationProvider>

            <ValidationProvider class="col-md-6 mb-3" name="Email" rules="required|email" v-slot="{ errors }">
                <label for="email" class="form-label">Email Address*</label>
                <div class="form-group mb-0">
                    <input
                        id="email"
                        type="email"
                        :disabled="isDisabled"
                        :class="['form-control mb-1', {'input-error' : errors[0] }]"
                        v-model="billing.email"
                    >
                </div>

                <div :class="['form-group field-validation-error', {'animated shake': errors[0] && animated }]" >
                    {{ errors[0] }}
                </div>
            </ValidationProvider>
        </ValidationObserver>

        <ValidationObserver class="row" ref="agreement">
            <ValidationProvider class="col-12 mb-3" name="Terms and Conditions" rules="required" v-slot="{ errors }">
                <div class="form-group">
                    <div class="form-check">
                        <input
                            id="agreement"
                            type="checkbox"
                            class="form-check-input-checkbox mb-1"
                            v-model="billing.agreement"
                        >
                        <label
                            for="agreement"
                            style="color: #000000; cursor: pointer;"
                            :class="['form-check-label', {'label-error' : errors[0], 'animated shake' : errors[0] && animated }]"
                        >
                            I have read and agree to the website <a href="/terms">terms and conditions</a>
                        </label>
                    </div>
                </div>
            </ValidationProvider>
        </ValidationObserver>
    </div>
</template>

<script>
    import resetAnimation from "@/js/mixins/reset-animation";

    export default {
        name: "PaymentBilling",
        props: {
            countries: {
                type: Array,
                required: true
            },
            isDisabled: {
                type: Boolean,
                required: true
            },
            order: {
                type: Object,
                required: true
            },
            states: {
                type: Array,
                required: true
            }
        },
        mixins:  [ resetAnimation ],
        data() {
            return {
                billing: {
                    first_name: null,
                    last_name: null,
                    company_name: null,
                    street: null,
                    country: null,
                    city: null,
                    state: null,
                    zip_code: null,
                    phone: null,
                    email: null,
                    agreement: null,
                },
            }
        },
        mounted() {
            this.resetForm();
        },
        methods: {
            resetForm() {
                const predefinedProps = ['first_name', 'last_name', 'country', 'phone'];

                for (const key in this.billing) {
                    if (key === 'country') {
                        this.billing[key] = 'United States';
                    } else if (predefinedProps.includes(key)) {
                        this.billing[key] = this.order[key];
                    } else {
                        this.billing[key] = null;
                    }
                }
            }
        }
    }
</script>

<style scoped>
    .field-validation-error {
        color: #d54141;
        font-family: Open Sans, sans-serif;
        font-size: 13px;
        font-weight: 400;
        margin-bottom: 0;
    }

    .input-error {
        border: 2px solid #ffb996 !important;
        background-color: #fff8f6 !important;
        border-radius: 3px !important;
    }

    .label-error {
        color: #d54141 !important;
    }
</style>
